import { Fragment, type FC, type ReactElement, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { Masonry } from 'dibs-elements/exports/Masonry.tsx';
import { ProductTileAd } from 'dibs-google-ad-manager/exports/ProductTileAd.tsx';
import { getProductTileAdData } from 'dibs-google-ad-manager/exports/adHelpers.ts';
import { HOME_MIXED_GRID_PLACEMENT } from 'dibs-google-ad-manager/exports/constants.ts';
import { CONSUMER, TRADE } from 'dibs-buyer-layout/exports/userTypeEnums.ts';
import { TILE_SIZES_PX } from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';
import { UserRecommendationsRecommendationTile } from 'dibs-personalized/exports/UserRecommendationsGrid.ts';
import { sanitizeAndDecodeHtml } from '../homepage/helpers/htmlHelpers.ts';
import { useHomePageContext } from '../homepage/HomePageContext.tsx';
import HpSharedItemsGridEditorialCollection from '../homepage/HpSharedItemsGrid/components/HpSharedItemsGridEditorialCollection.tsx';
import { HP_SHARED_ITEMS_GRID_ITEM_TYPES } from '../homepage/HpSharedItemsGrid/types.ts';
import {
    getHpPersonalizedMixedGridListingItemTrackingTrigger,
    getHpPersonalizedMixedGridProductTileSize,
    getHpPersonalizedMixedGridVisibleEdges,
    MIXED_GRID_DESKTOP_ITEMS_PER_ROW,
} from './buildHpPersonalizedMixedGrid.ts';

import { type HpPersonalizedMixedGridResp_viewer$key } from './__generated__/HpPersonalizedMixedGridResp_viewer.graphql.ts';

import { getHpPersonalizedMixedGridShimmerItems } from './HpPersonalizedMixedGridShimmer.tsx';
import styles from './HpPersonalizedMixedGridResp.module.css';
import classNames from 'classnames';
import dibsCss from 'dibs-css';

const AD_WIDTH = 300;

const MASONRY_PROPS = {
    breakpointToNumColsMap: {
        desktop: 4,
        tabletLandscape: 4,
        tabletPortrait: 4,
        mobile: 2,
    },
    numCols: 4,
    elementGap: 18,
} as const;

const viewerFragment = graphql`
    fragment HpPersonalizedMixedGridResp_viewer on Viewer
    @argumentDefinitions(
        recommendationType: { type: "UserRecommendationType!", defaultValue: PERSONALIZED }
        isPersonalizedContentAboveFoldTestVariant: { type: "Boolean!", defaultValue: false }
        mixedGridFirst: { type: "Int", defaultValue: 8 }
        mixedGridAfter: { type: "String" }
    ) {
        ...UserRecommendationsRecommendationTile_viewer
            @arguments(fetchFavorites: $hasUserId, userId: $userId)
        homepageMixedGrid(
            homepageId: $homepageId
            userId: $userId
            userType: $userType
            recommendationType: $recommendationType
            abTestList: $abTestList
            isMobile: $isMobile
        ) @include(if: $isPersonalizedContentAboveFoldTestVariant) {
            items(first: $mixedGridFirst, after: $mixedGridAfter)
                @connection(key: "HpPersonalizedMixedGrid_items") {
                edges {
                    node {
                        itemSource
                        item {
                            __typename
                            ... on Item {
                                serviceId
                                ...UserRecommendationsRecommendationTile_item
                                    @arguments(
                                        fetchFavorites: $hasUserId
                                        userId: $userId
                                        isTrade: $isTrade
                                        priceBookName: $priceBookName
                                    )
                            }
                            ... on EditorialItem {
                                cmsId
                                title
                                imageUrl
                                linkData {
                                    path
                                }
                            }
                            ... on HomepageMixedGridAdSlot {
                                # eslint-disable-next-line relay/unused-fields
                                slot
                            }
                        }
                    }
                }
            }
        }
        user(userId: $userId) @include(if: $hasUserId) {
            ...UserRecommendationsRecommendationTile_user
        }
    }
`;

type Props = {
    viewer: HpPersonalizedMixedGridResp_viewer$key;
    afterDisplay: boolean;
    currency: string;
    includeAd: boolean;
    onAbsentAd: (elementId: string) => void;
    shimmerCount: number;
    loadMoreTracker: ReactElement | null;
};

export const HpPersonalizedMixedGridResp: FC<Props> = ({
    viewer: viewerRef,
    afterDisplay,
    currency,
    includeAd,
    onAbsentAd,
    shimmerCount,
    loadMoreTracker,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const { isTrade } = useHomePageContext();
    const edges = getHpPersonalizedMixedGridVisibleEdges(
        (viewer.homepageMixedGrid?.items?.edges || []).filter(filterFalsy),
        includeAd
    );
    const adData = useMemo(
        () =>
            getProductTileAdData({
                uriRef: HOME_MIXED_GRID_PLACEMENT,
                userType: isTrade ? TRADE : CONSUMER,
                deviceType: 'desktop',
            })[0],
        [isTrade]
    );
    let productIndex = 0;
    let editorialItemIndex = 0;
    const gridItems = edges.map((edge, slotIndex) => {
        const node = edge.node;
        const item = node?.item;
        if (!node || !item) {
            return <Fragment key={`empty-${slotIndex}`} />;
        }

        if (item.__typename === 'EditorialItem') {
            editorialItemIndex += 1;
        }

        const productIndexForEdge = item.__typename === 'Item' ? productIndex++ : productIndex;

        switch (item.__typename) {
            case 'Item':
                return (
                    <UserRecommendationsRecommendationTile
                        key={item.serviceId || productIndexForEdge}
                        item={item}
                        viewer={viewer}
                        user={viewer.user}
                        index={productIndexForEdge}
                        itemsPerRow={MIXED_GRID_DESKTOP_ITEMS_PER_ROW}
                        isMobile={false}
                        trackingTrigger={getHpPersonalizedMixedGridListingItemTrackingTrigger(
                            node.itemSource
                        )}
                        dataTn="hp-personalized-mixed-grid"
                        currency={currency}
                        tileSize={getHpPersonalizedMixedGridProductTileSize(slotIndex)}
                        afterDisplay={afterDisplay}
                    />
                );
            case 'EditorialItem': {
                const imageUrl = item.imageUrl;
                const editorialTitle = item.title;
                const linkPath = item.linkData?.path;

                if (!imageUrl || !editorialTitle || !linkPath) {
                    return <Fragment key={`empty-${slotIndex}`} />;
                }

                return (
                    <HpSharedItemsGridEditorialCollection
                        key={`collection-${item.cmsId ?? editorialItemIndex - 1}`}
                        item={{
                            type: HP_SHARED_ITEMS_GRID_ITEM_TYPES.EDITORIAL_COLLECTION,
                            imageUrl,
                            title: sanitizeAndDecodeHtml(editorialTitle),
                            linkPath,
                            editorialModuleOrder: 1,
                            editorialItemIndex: editorialItemIndex - 1,
                        }}
                    />
                );
            }
            case 'HomepageMixedGridAdSlot':
                if (!includeAd || !adData) {
                    return <Fragment key={`empty-${slotIndex}`} />;
                }

                return (
                    <div
                        key={`hp-personalized-mixed-grid-ad-${item.slot}`}
                        className={classNames(dibsCss.bgMoonstone, styles.adPlaceholder)}
                        style={{
                            height: TILE_SIZES_PX.xxxLarge,
                            minHeight: TILE_SIZES_PX.xxxLarge,
                            minWidth: AD_WIDTH,
                        }}
                        data-tn="hp-personalized-mixed-grid-ad"
                    >
                        <ProductTileAd
                            {...adData}
                            height={TILE_SIZES_PX.xxxLarge}
                            heightHold={TILE_SIZES_PX.xxxLarge}
                            containerClassName={styles.adContainer}
                            onAbsentAd={onAbsentAd}
                        />
                    </div>
                );
            default:
                return <Fragment key={`empty-${slotIndex}`} />;
        }
    });

    return (
        <Masonry {...MASONRY_PROPS}>
            {[
                ...gridItems,
                ...getHpPersonalizedMixedGridShimmerItems({
                    count: shimmerCount,
                    startIndex: edges.length,
                    isMobile: false,
                    loadMoreTracker,
                }),
            ]}
        </Masonry>
    );
};
